import Persistable from "../../../storage/persistable";

/* This class is a summary of GameContext.
Be careful adding new props to this class to avoid to populate them to GameContext when it is restored from Snapshot */

/**
 * Freeze a clock at its current effective time. Returns a clone that is
 * paused with referenceValue = source.now() and no referenceTime. The
 * original clock is unchanged. This prevents snapshots that capture a
 * running clock from drifting to 0 when revived later (wall-clock delta
 * since referenceTime would otherwise be huge).
 */
const freezeClock = (clock) => {
    if (!clock) return null;
    const cloned = Object.create(Object.getPrototypeOf(clock));
    Object.assign(cloned, clock);
    const currentMs = typeof clock.now === "function" ? clock.now() : 0;
    if ("referenceValue" in cloned) {
        cloned.referenceValue = currentMs;
    }
    if ("startTime" in cloned) {
        cloned.startTime = currentMs;
    }
    if ("referenceTime" in cloned) {
        cloned.referenceTime = null;
    }
    if ("paused" in cloned) {
        cloned.paused = true;
    }
    return cloned;
};

export default class FootballGameContextSnapshot extends Persistable {
    constructor({
        firstDownAt,
        fieldPosition,
        homeTeamHasPossession,
        down,
        gameClock,
        score,
        timeouts,
        triggerPAT,
        currentDriveId,
        driveContext,
        currentPeriod,
        swapTeams,
        keepSwappingTeams,
        kickingOffTeamId,
        firstQtrKickoffTeamId,
        triggerKO,
        fbOtDisabled,
        homeTeamId,
        awayTeamId,
        coinTossRequired,
        ruleSetId
    } = {}) {
        super();
        this.down = down;
        this.fieldPosition = fieldPosition;
        // Freeze the clock at this moment. Snapshots must NOT hold a live
        // reference to a running clock — when revived later, the wall-clock
        // delta would have advanced and the time would drift (or clamp to 0).
        this.gameClock = freezeClock(gameClock);
        this.homeTeamHasPossession = homeTeamHasPossession;
        this.firstDownAt = firstDownAt;
        this.score = score;
        this.timeouts = timeouts;
        this.triggerPAT = triggerPAT;
        this.currentDriveId = currentDriveId;
        this.driveContext = driveContext;
        this.currentPeriod = currentPeriod;
        this.swapTeams = swapTeams;
        this.keepSwappingTeams = keepSwappingTeams;
        this.kickingOffTeamId = kickingOffTeamId;
        this.firstQtrKickoffTeamId = firstQtrKickoffTeamId;
        this.triggerKO = triggerKO;
        this.fbOtDisabled = fbOtDisabled;
        this.homeTeamId = homeTeamId;
        this.awayTeamId = awayTeamId;
        this.coinTossRequired = coinTossRequired;
        this.ruleSetId = ruleSetId;
    }

    isHomePossession() {
        return this.homeTeamHasPossession;
    }

    isVisitorPossession() {
        return !this.homeTeamHasPossession;
    }

    getOpponent(teamId) {
        return teamId === this.homeTeamId ? this.awayTeamId : this.homeTeamId;
    }

    getPossessionTeamId() {
        return this.isHomePossession() ? this.homeTeamId : this.awayTeamId;
    }

    getTime() {
        return this.gameClock.now();
    }

    setClockSeconds(seconds) {
        this.gameClock.setSeconds(seconds);
    }
}
